import React from "react";
import PropTypes from "prop-types";
import { UPLOAD_STATUS } from "../hooks/useFileUpload";
import "./UploadProgressPanel.scss";

import {
  CheckCircleFilledIcon,
  FailCircleFilledIcon,
  PendingCircleIcon,
  ShieldIcon,
} from "../../../icons";

// CSS-only spinner — not an SVG icon
const Spinner = () => <span className="upp-spin" aria-hidden="true" />;

function StepRow({ label, rowState, progress, statusLabel: _statusLabel, isVerify = false }) {
  const showBar = (rowState === "active" || rowState === "done") && !isVerify;
  const barWidth = rowState === "done" ? 100 : (progress ?? 0);

  return (
    <div className={`upp-step upp-step--${rowState}`}>
      {/* ── Left: status icon ── */}
      <div className="upp-step-icon">
        {rowState === "done" && <CheckCircleFilledIcon size={16} />}
        {rowState === "failed" && <FailCircleFilledIcon size={16} />}
        {rowState === "active" && <Spinner />}
        {rowState === "pending" && <PendingCircleIcon size={16} />}
      </div>

      {/* ── Centre: label + bar ── */}
      <div className="upp-step-body">
        <div className="upp-step-header">
          <span className="upp-step-label">{label}</span>

          {/* Right-side status badge */}
          <span className={`upp-step-badge upp-step-badge--${rowState}`}>
            {rowState === "active" && !isVerify && <>{progress ?? 0}%</>}
            {rowState === "active" && isVerify && "In Progress"}
            {rowState === "done" && (isVerify ? "Verified" : "Completed")}
            {rowState === "failed" && "Failed"}
            {rowState === "pending" && "Pending"}
          </span>
        </div>

        {/* Progress bar (video + srt only — not verify row) */}
        {showBar && (
          <div
            className="upp-bar-track"
            role="progressbar"
            aria-valuenow={barWidth}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={`upp-bar-fill${rowState === "done" ? " upp-bar-fill--done" : ""}`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
        )}

        {/* Pending placeholder line */}
        {rowState === "pending" && <div className="upp-bar-track upp-bar-track--ghost" />}
      </div>
    </div>
  );
}

// UploadProgressPanel
// ─────────────────────────────────────────────────────────────────────────────
function UploadProgressPanel({ uploadStatus, videoProgress, srtProgress, uploadError }) {
  const isUploading = uploadStatus === UPLOAD_STATUS.UPLOADING;
  const isVerifying = uploadStatus === UPLOAD_STATUS.VERIFYING;
  const isComplete = uploadStatus === UPLOAD_STATUS.UPLOAD_COMPLETE;
  const isFailed = uploadStatus === UPLOAD_STATUS.UPLOAD_FAILED;

  // Video row
  const videoState = (() => {
    if (isComplete || isVerifying) return "done";
    if (isFailed && videoProgress >= 100) return "done";
    if (isFailed && videoProgress > 0) return "failed";
    if (isFailed) return "failed";
    if (isUploading && videoProgress > 0) return "active";
    if (isUploading) return "active";
    return "pending";
  })();

  // SRT row
  const srtState = (() => {
    if (isComplete || isVerifying) return "done";
    if (isFailed && srtProgress >= 100) return "done";
    if (isFailed && srtProgress > 0) return "failed";
    if (isFailed) return "failed";
    if (isUploading && srtProgress > 0) return "active";
    if (isUploading) return "active";
    return "pending";
  })();

  // Verify row
  const verifyState = (() => {
    if (isComplete) return "done";
    if (isVerifying) return "active";
    if (isFailed && (isVerifying || uploadStatus === UPLOAD_STATUS.VERIFYING)) return "failed";
    // Failed after verifying started
    if (isFailed && videoProgress >= 100 && srtProgress >= 100) return "failed";
    return "pending";
  })();

  return (
    <div className="upp-panel">
      {/* ── Panel heading ── */}
      <div className="upp-panel-header">
        <span className="upp-panel-icon">
          {isComplete ? (
            <CheckCircleFilledIcon size={16} />
          ) : isFailed ? (
            <FailCircleFilledIcon size={16} />
          ) : isVerifying ? (
            <ShieldIcon size={14} />
          ) : (
            <Spinner />
          )}
        </span>
        <span className="upp-panel-title">
          {isComplete && "Upload Complete"}
          {isFailed && "Upload Failed"}
          {isVerifying && "Verifying Upload…"}
          {isUploading && "Uploading Files…"}
        </span>
      </div>

      {/* ── Step rows ── */}
      <div className="upp-steps">
        <StepRow label="Uploading Video" rowState={videoState} progress={videoProgress} />

        <div className="upp-divider" />

        <StepRow label="Uploading Subtitle" rowState={srtState} progress={srtProgress} />

        <div className="upp-divider" />

        <StepRow label="Verifying Upload" rowState={verifyState} isVerify />
      </div>

      {/* ── Error detail (UPLOAD_FAILED only) ── */}
      {isFailed && uploadError && (
        <div className="upp-error-detail" role="alert">
          <FailCircleFilledIcon size={16} />
          <span>{uploadError}</span>
        </div>
      )}
    </div>
  );
}

StepRow.propTypes = {
  label: PropTypes.string,
  rowState: PropTypes.string,
  progress: PropTypes.number,
  statusLabel: PropTypes.string,
  isVerify: PropTypes.bool,
};

UploadProgressPanel.propTypes = {
  uploadStatus: PropTypes.string,
  videoProgress: PropTypes.number,
  srtProgress: PropTypes.number,
  uploadError: PropTypes.string,
};

export default UploadProgressPanel;
